import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'filled' | 'outlined' | 'compact' | 'interactive' | 'glass';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding,
      shadow,
      border,
      children,
      ...props
    },
    ref
  ) => {
    // M3 Shape: 16px radius for modern SaaS cards
    const baseClasses = [
      'rounded-2xl',
      'transition-all',
      'duration-200',
      'overflow-hidden',
    ];

    // High-Tech Glass Card variants:
    const variantClasses = {
      default: [
        'bg-card',
        'border border-border/80',
        'shadow-xs',
        'backdrop-blur-xl',
      ],
      elevated: [
        'bg-card',
        'border border-border/70',
        'shadow-sm',
        'backdrop-blur-xl',
        'hover:shadow-md',
        'hover:-translate-y-0.5',
      ],
      filled: [
        'bg-secondary/60',
        'border border-border/50',
      ],
      outlined: [
        'bg-card/40',
        'backdrop-blur-md',
        'border border-border',
      ],
      compact: [
        'bg-card',
        'border border-border/60',
        'shadow-2xs',
      ],
      glass: [
        'bg-card/75',
        'backdrop-blur-xl',
        'border border-border/60',
        'shadow-xs',
      ],
      interactive: [
        'bg-card',
        'border border-border/80',
        'shadow-xs',
        'backdrop-blur-xl',
        'cursor-pointer',
        'hover:shadow-md',
        'hover:border-primary/40',
        'hover:-translate-y-0.5',
        'active:scale-[0.995]',
      ],
    };

    const paddingValue = padding || (variant === 'compact' ? 'sm' : 'md');

    const paddingClasses = {
      none: [],
      sm: ['p-3.5'],
      md: ['p-5'],
      lg: ['p-6'],
    };

    const classes = clsx(
      baseClasses,
      variantClasses[variant],
      paddingClasses[paddingValue],
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card sub-components
const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('flex flex-col space-y-1', className)} // Reduced spacing
      {...props}
    >
      {children}
    </div>
  )
);

CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={clsx('text-base font-semibold text-foreground tracking-tight', className)} // Smaller title, tight tracking
      {...props}
    >
      {children}
    </h3>
  )
);

CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={clsx('text-sm text-muted-foreground', className)}
      {...props}
    >
      {children}
    </p>
  )
);

CardDescription.displayName = 'CardDescription';

const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx('pt-2', className)} {...props}> {/* Reduced top padding */}
      {children}
    </div>
  )
);

CardContent.displayName = 'CardContent';

const CardFooter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx('flex items-center pt-4', className)}
      {...props}
    >
      {children}
    </div>
  )
);

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };